/**
 * Разбор событий веб-сокета.
 *
 * Сервер присылает в один и тот же канал `newMessage` две разные вещи:
 * обычное сообщение чата и системное событие (`userStatusChanged`, `typing`).
 * Часть полей сообщения лежит в корне, часть — во вложенном `payload`, а
 * направление (`direction`) приходит не во всех каналах.
 *
 * Раньше каждый компонент разбирал эту структуру по-своему, поэтому одно и
 * то же сообщение в списке чатов считалось входящим, а в открытом чате — нет,
 * и наоборот. Здесь единые правила чтения payload'а.
 */

/** Полезная нагрузка, которую может прислать веб-сокет. */
export interface WsPayload {
    /** Тип системного события; у обычного сообщения отсутствует. */
    event?: string;
    /** ID сообщения (у событий его нет). */
    id?: string | null;
    /** ID беседы в корне (может отсутствовать — тогда смотрим payload). */
    conversationId?: string | null;
    text?: string | null;
    content?: string | null;
    /** `inbound`/`incoming` — от клиента, `outbound`/`outgoing` — от нас. */
    direction?: string | null;
    senderId?: string | number | null;
    senderName?: string | null;
    sentAt?: string | null;
    /** Для `userStatusChanged` и `typing`. */
    userId?: string | number | null;
    status?: string | null;
    lastSeen?: string | null;
    payload?: WsPayload | null;
}

/** Направления, которые означают «написал клиент». */
const INBOUND_DIRECTIONS = ['inbound', 'incoming'];

/** Направления, которые означают «написал сотрудник». */
const OUTBOUND_DIRECTIONS = ['outbound', 'outgoing'];

/** ID беседы: сервер кладёт его в корень, но иногда только в payload. */
export const getMessageConversationId = (message: WsPayload | null | undefined): string | null => {
    if (!message) return null;
    const id = message.conversationId || message.payload?.conversationId;
    return id ? String(id) : null;
};

/** Текст сообщения; для вложения текст может отсутствовать. */
export const getMessageText = (message: WsPayload | null | undefined): string => {
    if (!message) return '';
    return String(message.text || message.payload?.text || message.content || '');
};

/** ID автора сообщения в нижнем регистре (ID сравниваем без учёта регистра). */
export const getSenderId = (message: WsPayload | null | undefined): string => {
    if (!message) return '';
    const id = message.senderId || message.payload?.senderId;
    return id === null || id === undefined ? '' : String(id).toLowerCase();
};

/** Системное событие (статус, «печатает»), а не сообщение. */
export const isSystemEvent = (message: WsPayload | null | undefined): boolean => !!message?.event;

/**
 * Сообщение пришло от клиента.
 *
 * Основной признак — `direction`: сервер проставляет его всегда. Если поля
 * нет (старые сборки бэкенда), сравниваем автора с текущим пользователем.
 * Раньше сравнение ломалось, когда `senderId` отсутствовал: строка
 * `String(undefined) !== String(user.id)` всегда истинна, поэтому собственные
 * сообщения считались входящими и увеличивали счётчик.
 */
export const isIncomingMessage = (
    message: WsPayload | null | undefined,
    currentUserId?: string | number | null
): boolean => {
    if (!message || isSystemEvent(message)) return false;

    const direction = String(message.direction || '').toLowerCase();
    if (INBOUND_DIRECTIONS.includes(direction)) return true;
    if (OUTBOUND_DIRECTIONS.includes(direction)) return false;

    const senderId = getSenderId(message);
    if (!senderId) return false;
    return senderId !== String(currentUserId ?? '').toLowerCase();
};

/** Ключ дедупликации: одно и то же сообщение не должно обрабатываться дважды. */
export const messageEventKey = (message: WsPayload | null | undefined): string | null => {
    if (!message) return null;

    if (isSystemEvent(message)) {
        // У события нет id, поэтому собираем ключ из его содержимого.
        const status = String(message.status || '').toLowerCase();
        const eventUserId = String(message.userId ?? '').toLowerCase();
        const conversationId = getMessageConversationId(message) || '';
        return `event:${message.event}:${eventUserId}:${status}:${conversationId}`;
    }

    if (message.id) return String(message.id);

    // Сообщение без id (например, вложение из старого вебхука): опираемся на
    // беседу, текст и время — этого достаточно, чтобы отличить два разных.
    const conversationId = getMessageConversationId(message) || '';
    return `msg:${conversationId}:${getMessageText(message)}:${message.sentAt || ''}`;
};
